import type React from "react";
import { useEffect, useState } from "react";

import { Pager, useFitPages } from "../ui/paging";
import styles from "./operator-page.module.css";
import { markPaid, operatorBooks } from "./operator-api";
import type { OperatorBooks } from "./operator-api";
import { RecordPaymentDialog, monthName } from "./payments-panel";
import { priceText } from "./price-format";

/** `2026-10` moved by `by` months. */
function shift(month: string, by: number): string {
  const d = new Date(`${month}-01T00:00:00.000Z`);
  d.setUTCMonth(d.getUTCMonth() + by);
  return d.toISOString().slice(0, 7);
}

/**
 * **The operator's own books** (BP-D3, Emile 2026-10-17: *"take the accounting
 * module … and add it to my operator console"* — answered as *your own books*).
 *
 * One month at a time: what every clinic was billed, what was recorded as paid,
 * and what is still outstanding, then the clinics themselves, unpaid first. A
 * row opens that clinic. The clinics keep their own Accounting add-on; this is
 * DentaCore's business, not theirs.
 */
export function AccountingSection({
  onOpenClinic,
}: {
  onOpenClinic: (clinicId: string) => void;
}): React.JSX.Element {
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [books, setBooks] = useState<OperatorBooks | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    setBooks(null);
    void operatorBooks(month)
      .then(setBooks)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load the books");
      });
  }, [month, version]);

  // Unpaid first, so what needs chasing is on the first page.
  const rows = [...(books?.rows ?? [])].sort(
    (a, b) =>
      Number(a.payment !== null) - Number(b.payment !== null) || a.name.localeCompare(b.name),
  );
  const { areaRef, measure, page, setPage, pages, range } = useFitPages({
    keys: rows.map((r) => r.clinicId),
    estimate: () => 50,
    gap: 0,
    resetOn: month,
  });
  const thisMonth = new Date().toISOString().slice(0, 7);

  return (
    <div className={`${styles.console} ${styles.fitPage}`} data-testid="operator-accounting">
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Accounting</h1>
          <p className={styles.muted}>What your clinics were billed and what they paid you.</p>
        </div>
        <div className={styles.monthSwitch}>
          <button
            type="button"
            className={styles.primary}
            disabled={books === null || books.rows.length === 0}
            onClick={() => setRecording(true)}
            data-testid="books-record-payment"
          >
            Record a payment
          </button>
          <button
            type="button"
            className={styles.ghost}
            aria-label="The month before"
            onClick={() => setMonth((m) => shift(m, -1))}
            data-testid="books-prev"
          >
            ‹
          </button>
          <span className={styles.monthLabel} data-testid="books-month">
            {monthName(month)}
          </span>
          <button
            type="button"
            className={styles.ghost}
            aria-label="The month after"
            disabled={month >= thisMonth}
            onClick={() => setMonth((m) => shift(m, 1))}
            data-testid="books-next"
          >
            ›
          </button>
        </div>
      </header>

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <div className={styles.figures}>
        <div className={styles.figure}>
          <span className={styles.figureLabel}>Billed</span>
          <span className={styles.figureValue} data-testid="books-billed">
            {books === null ? "…" : priceText(books.billedCents)}
          </span>
        </div>
        <div className={styles.figure}>
          <span className={styles.figureLabel}>Collected</span>
          <span className={styles.figureGood} data-testid="books-collected">
            {books === null ? "…" : priceText(books.collectedCents)}
          </span>
        </div>
        <div className={styles.figure}>
          <span className={styles.figureLabel}>Outstanding</span>
          <span
            className={
              books !== null && books.outstandingCents > 0 ? styles.figureWarn : styles.figureValue
            }
            data-testid="books-outstanding"
          >
            {books === null ? "…" : priceText(books.outstandingCents)}
          </span>
        </div>
        <div className={styles.figure}>
          <span className={styles.figureLabel}>No price set</span>
          <span className={styles.figureValue} data-testid="books-unpriced">
            {books === null ? "…" : books.unpricedCount}
          </span>
        </div>
      </div>

      {recording && books !== null && (
        <RecordPaymentDialog
          choices={rows.map((r) => ({
            key: r.clinicId,
            label: r.name,
            billCents: r.billCents,
            payment: r.payment,
          }))}
          chooseLabel={`Clinic, for ${monthName(month)}`}
          initialKey={rows.find((r) => r.payment === null)?.clinicId ?? rows[0]?.clinicId ?? ""}
          onClose={() => setRecording(false)}
          onSave={(clinicId, body) =>
            markPaid(clinicId, month, body).then(() => {
              setRecording(false);
              setVersion((v) => v + 1);
            })
          }
        />
      )}

      <section className={styles.tabCard}>
        <div className={styles.tabBody}>
          <div className={styles.booksHead} aria-hidden="true">
            <span>Clinic</span>
            <span>Bill</span>
            <span>Paid</span>
          </div>
          <div className={styles.fitList} ref={areaRef}>
            <ul className={styles.rows} data-testid="books-list">
              {rows.slice(range[0], range[1]).map((row) => (
                <li key={row.clinicId} ref={measure(row.clinicId)}>
                  <button
                    type="button"
                    className={styles.booksRow}
                    onClick={() => onOpenClinic(row.clinicId)}
                    data-card=""
                    data-testid="books-row"
                  >
                    <span className={styles.rowName}>
                      {row.name}
                      {row.isDemo && <span className={styles.rowSub}> · demo</span>}
                    </span>
                    <span className={styles.figureCell}>
                      {row.billCents === null ? (
                        <span className={styles.muted}>No price set</span>
                      ) : (
                        priceText(row.billCents)
                      )}
                    </span>
                    <span>
                      {row.payment !== null ? (
                        <span className={styles.chipOk}>
                          Paid {priceText(row.payment.amountCents)}
                        </span>
                      ) : month < thisMonth ? (
                        <span className={styles.chipWarn}>Unpaid</span>
                      ) : (
                        <span className={styles.muted}>Not yet</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <Pager
            page={page}
            count={pages.length}
            onPage={setPage}
            from={range[0] + 1}
            to={range[1]}
            total={rows.length}
            noun="clinics"
            testId="books-pager"
          />
        </div>
      </section>
    </div>
  );
}
